/* eslint react-hooks/exhaustive-deps: 0 */
import React, {
  useImperativeHandle,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useContext,
} from 'react';

import Section from '../Section';
import Input from '../Input';
import { Row } from '../../styles/utils/Grid';
import {
  useInputState,
  useMaritalStates,
  useProfessions,
  useIncomeGroups,
} from '../../hooks';
import masks from '../../utils';
import { handleChange } from '../../utils/inputEvents';
import Autocomplete, { MinLengthAutocomplete } from '../Autocomplete';
import { formatDate, dateToStringFormat } from '../../utils/formatters';
import {
  required,
  validCpf,
  validBirthdate,
  validBirthdateRange,
  validBirthdateDay,
  minLength,
  hasSurname,
  validAge,
} from '../../utils/inputValidation';
import {
  personalDataTitle,
  nameInputPlaceholder,
  sexInputPlaceholder,
  cpfInputPlaceholder,
  birthdatePlaceholder,
  maritalStatePlaceholder,
  professionPlaceholder,
  individualIncomePlaceholder,
  familyIncomePlaceholder,
  individualIncomeError,
} from '../../utils/constants';
import { AppContext } from '../../context';

const PersonalDataPF = ({ setContext, personalData }, ref) => {
  const { ibcLogged } = useContext(AppContext);
  const [name, setName, nameRef, validateName] = useInputState(
    personalData?.nome
  );
  const [cpf, setCPF, cpfRef, validateCpf] = useInputState(
    personalData?.identificacao.codigo
  );
  const { loading, maritalStates } = useMaritalStates();
  const { loading: loadingProfessions, professions } = useProfessions();
  const { loading: loadingIncomeGroups, incomeGroups } = useIncomeGroups();
  const [
    birthdate,
    setBirthdate,
    birthdateRef,
    validateBirthdate,
  ] = useInputState(
    dateToStringFormat(personalData?.dadosPessoaFisica.dataNascimento ?? '')
  );
  const [sex, setSex, sexRef, validateSex] = useInputState(
    personalData?.dadosPessoaFisica.sexo.codigo
  );
  const [
    maritalState,
    setMaritalState,
    maritalStateRef,
    validateMaritalState,
  ] = useInputState(personalData?.dadosPessoaFisica.estadoCivil.codigo);
  const [
    profession,
    setProfession,
    professionRef,
    validateProfession,
  ] = useInputState(
    personalData?.dadosPessoaFisica.profissao.codigo &&
      parseInt(personalData.dadosPessoaFisica.profissao.codigo, 10)
  );
  const [
    individualIncome,
    setIndividualIncome,
    individualIncomeRef,
    validateIndividualIncome,
  ] = useInputState(personalData?.informacoesEconomicas.faixaRenda[1].codigo);
  const [
    familyIncome,
    setFamilyIncome,
    familyIncomeRef,
    validateFamilyIncome,
  ] = useInputState(personalData?.informacoesEconomicas.faixaRenda[0].codigo);
  useLayoutEffect(() => {
    setContext({
      loading: loading || loadingProfessions || loadingIncomeGroups,
    });
  }, [loading, loadingProfessions, loadingIncomeGroups, setContext]);

  useImperativeHandle(
    ref,
    () => ({
      validate: () => {
        const isInvalid = [
          validateName,
          validateBirthdate,
          validateCpf,
          validateFamilyIncome,
          validateIndividualIncome,
          validateProfession,
          validateSex,
          validateMaritalState,
        ]
          .reverse()
          .reduce((acc, v) => {
            const invalid = v();
            return acc || invalid;
          }, false);

        return {
          invalid: isInvalid,
          value: isInvalid
            ? ''
            : {
                identificacao: {
                  tipo: 'F',
                  documento: 'CPF',
                  codigo: cpf.value,
                },
                nome: name.value,
                dadosPessoaFisica: {
                  dataNascimento: formatDate(birthdate.value),
                  sexo: {
                    codigo: sex.value,
                  },
                  estadoCivil: {
                    codigo: maritalState.value,
                    valor: maritalStates.find(
                      ms => ms.codigo === maritalState.value
                    ).descricao,
                  },
                  profissao: {
                    codigo: `${profession.value}`,
                    valor: professions.find(
                      prof => prof.codigo === profession.value
                    ).nome,
                  },
                },
                informacoesEconomicas: {
                  faixaRenda: [
                    {
                      codigo: familyIncome.value,
                      descricao: incomeGroups.obj_renda_familiar.find(
                        ig => ig.seq_faixa_renda === familyIncome.value
                      ).des_faixa_renda,
                      tipoRenda: 'familiar',
                    },
                    {
                      codigo: individualIncome.value,
                      descricao: incomeGroups.obj_renda_familiar.find(
                        ig => ig.seq_faixa_renda === individualIncome.value
                      ).des_faixa_renda,
                      tipoRenda: 'individual',
                    },
                  ],
                },
              },
        };
      },
    }),
    [
      validateName,
      validateBirthdate,
      validateCpf,
      validateFamilyIncome,
      validateIndividualIncome,
      validateProfession,
      validateSex,
      validateMaritalState,
      birthdate,
      cpf,
      individualIncome,
      familyIncome,
      maritalState,
      name,
      profession,
      sex,
      maritalStates,
      professions,
      incomeGroups.obj_renda_familiar,
    ]
  );

  useEffect(() => {
    if (
      parseInt(individualIncome.value, 10) > parseInt(familyIncome.value, 10)
    ) {
      setFamilyIncome({
        ...familyIncome,
        invalid: true,
        errorMessage: individualIncomeError,
      });
      return;
    }
    setFamilyIncome({
      ...familyIncome,
      invalid: false,
    });
  }, [individualIncome.value, setFamilyIncome]);

  useEffect(() => {
    if (individualIncome.value === '') {
      return;
    }

    if (
      parseInt(individualIncome.value, 10) > parseInt(familyIncome.value, 10)
    ) {
      setIndividualIncome({
        ...individualIncome,
        invalid: true,
        errorMessage: individualIncomeError,
      });
      return;
    }
    setIndividualIncome({
      ...individualIncome,
      invalid: false,
    });
  }, [familyIncome, setIndividualIncome]);

  return (
    <Section title={personalDataTitle}>
      <Row>
        <Input
          placeholder={nameInputPlaceholder}
          value={name.value}
          invalid={name.invalid}
          errorMessage={name.errorMessage}
          onChange={handleChange(setName)}
          mask={masks.name}
          validations={[required, minLength(3), hasSurname]}
          ref={nameRef}
          disabled={ibcLogged}
          maxLength={50}
        />
      </Row>
      <Row columns={3}>
        <Autocomplete
          placeholder={sexInputPlaceholder}
          options={[
            { codigo: 'F', descricao: 'Feminino' },
            { codigo: 'M', descricao: 'Masculino' },
          ]}
          setter={setSex}
          value={sex.value}
          validations={[required]}
          getOptionLabel={option => option.descricao}
          getOptionValue={option => option.codigo}
          ref={sexRef}
          invalid={sex.invalid}
          errorMessage={sex.errorMessage}
        />

        <Input
          placeholder={cpfInputPlaceholder}
          value={cpf.value}
          invalid={cpf.invalid}
          errorMessage={cpf.errorMessage}
          onChange={handleChange(setCPF)}
          maxLength="14"
          mask={masks.cpf}
          validations={[required, validCpf]}
          ref={cpfRef}
          disabled={ibcLogged}
        />

        <Input
          placeholder={birthdatePlaceholder}
          value={birthdate.value}
          invalid={birthdate.invalid}
          errorMessage={birthdate.errorMessage}
          onChange={handleChange(setBirthdate)}
          validations={[
            required,
            validBirthdate,
            validBirthdateDay,
            validBirthdateRange,
            validAge(16, 100),
          ]}
          mask={masks.date}
          maxLength="10"
          ref={birthdateRef}
          disabled={ibcLogged}
        />
      </Row>
      <Row columns={2}>
        <Autocomplete
          options={maritalStates}
          placeholder={maritalStatePlaceholder}
          value={maritalState.value}
          setter={setMaritalState}
          validations={[required]}
          getOptionLabel={option => option.descricao}
          getOptionValue={option => option.codigo}
          ref={maritalStateRef}
          invalid={maritalState.invalid}
          errorMessage={maritalState.errorMessage}
        />

        <MinLengthAutocomplete
          options={professions}
          placeholder={professionPlaceholder}
          value={profession.value}
          setter={setProfession}
          validations={[required]}
          getOptionLabel={option => option.nome}
          getOptionValue={option => option.codigo}
          autocompleteRef={professionRef}
          invalid={profession.invalid}
          errorMessage={profession.errorMessage}
        />
      </Row>
      <Row columns={2}>
        <Autocomplete
          options={incomeGroups.obj_renda_familiar ?? []}
          placeholder={individualIncomePlaceholder}
          value={individualIncome.value}
          setter={setIndividualIncome}
          validations={[required]}
          getOptionLabel={option => option.des_faixa_renda}
          getOptionValue={option => option.seq_faixa_renda}
          ref={individualIncomeRef}
          invalid={individualIncome.invalid}
          errorMessage={individualIncome.errorMessage}
          readOnly
        />

        <Autocomplete
          options={incomeGroups.obj_renda_familiar ?? []}
          placeholder={familyIncomePlaceholder}
          value={familyIncome.value}
          setter={setFamilyIncome}
          validations={[required]}
          getOptionLabel={option => option.des_faixa_renda}
          getOptionValue={option => option.seq_faixa_renda}
          ref={familyIncomeRef}
          invalid={familyIncome.invalid}
          errorMessage={familyIncome.errorMessage}
        />
      </Row>
    </Section>
  );
};

export default forwardRef(PersonalDataPF);
